import { LinkIcon } from '@heroicons/react/20/solid';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { HeroButton as Button, extendVariants } from '@cavela/ui';
import { ROUTE_PATHS } from 'src/shared/config/routes';

const StyledLink = extendVariants(Button, {
  variants: {
    color: {
      link: 'bg-white text-dark-4 tracking-[-0.14px] hover:!opacity-50'
    },
    isDisabled: {
      true: '!text-secondary-text cursor-not-allowed opacity-100'
    },
    size: {
      lg: '!p-[4px] max-h-[30px] gap-[4px] !min-w-max w-fit border border-gray-6 break-all rounded-lg'
    }
  },

  defaultVariants: {
    color: 'link',
    size: 'lg',
    radius: 'lg',
    as: 'a'
  }
});

interface LinkProps {
  to: (typeof ROUTE_PATHS)[keyof typeof ROUTE_PATHS] | string;
  children: React.ReactNode;
  isDisabled?: boolean;
  isExternal?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

const Link: React.FC<LinkProps> = ({
  to,
  children,
  isDisabled,
  isExternal,
  className,
  icon,
  ...props
}) => {
  const navigate = useNavigate();

  const onNavigate = useCallback(() => {
    if (isExternal) {
      window.open(to, '_blank');
    } else {
      navigate(to);
    }
  }, [navigate, to, isExternal]);

  return (
    <StyledLink
      isDisabled={isDisabled}
      onPress={!isDisabled ? onNavigate : undefined}
      startContent={
        icon ?? <LinkIcon className="size-4 pointer-events-none text-primary" />
      }
      className={className}
      {...props}
    >
      {children}
    </StyledLink>
  );
};

export default Link;
